import { useState, useEffect } from "react";
import { UXDataTable } from "../../components/UXDataTable";
import { applyFiltersToArray } from "../../global";
import { ProgressSpinner } from "../../components/ProgressSpinner";
import ActionModal from "./ActionModal";
import { useDistillationExperimentActions } from "./hooks/useDistillationExperimentActions";
import { useAuth } from "../../context/AuthContext";
import { Roles } from "../../constants/roles";

type DistillationRow = {
  id: number;
  distillationStatusName?: string;
  initialContainer?: {
    sampleName?: string;
  };
  [key: string]: any;
};

type Filter = {
  [key: string]: any;
};

interface TableDistillationsBacklogProps {
  filters: Filter[];
  myExperiments: DistillationRow[];
  isDataLoading: boolean;
  getOpenExperiments: () => void;
}

const MyDistillationCols = [
  { field: "id", headerName: "ID", type: "number" },
  { field: "distillationTypeName", headerName: "Type", type: "label" },
  { field: "initialContainer.sampleName", headerName: "Feed MIDAS #", type: "object-midasnumber" },
  { field: "requestedByEmail", headerName: "Requester", type: "label" },
  { field: "mnemonic", headerName: "Mnemonic", type: "label" },
  { field: "comments", headerName: "Comments", type: "label" },
  { field: "experimentStartDate", headerName: "Start Date", type: "date" },
  { field: "createdDate", headerName: "Submitted On", type: "date" },
  { field: "requestedCompletionDate", headerName: "Req. Completion Date", type: "date" },
  { field: "distillationStatusName", headerName: "Status", type: "label" },
];

const TableDistillationsBacklog = ({
  filters,
  myExperiments,
  isDataLoading,
  getOpenExperiments,
}: TableDistillationsBacklogProps) => {
  const [filteredExperiments, setFilteredExperiments] = useState<DistillationRow[]>([]);
  const noDataMessage = `No experiments found in an state < Complete`;

  const { hasAnyRole } = useAuth();
  const isDistillationWorker = hasAnyRole([Roles.DistillationWorker, Roles.Developer]);

  const {
    isProcessingAction,
    activeModal,
    closeModal,
    canReturnToRequester,
    canCancelExperiment,
    startReturnToRequester,
    startCancelExperiment,
  } = useDistillationExperimentActions({
    onActionComplete: getOpenExperiments,
  });

  const menuItems = [
    {
      menuType: "link-processDistillation",
      redirectPath: "/processDistillation",
      text: "Process Experiment",
      onClickFunction: null,
      isDisabled: !isDistillationWorker,
    },
    {
      menuType: "text",
      redirectPath: "/",
      text: "Send Back to Requester",
      onClickFunction: startReturnToRequester,
      isDisabled: (row: DistillationRow) => !isDistillationWorker || !canReturnToRequester(row),
    },
    {
      menuType: "text",
      redirectPath: "",
      text: "Cancel Experiment",
      onClickFunction: startCancelExperiment,
      isDisabled: (row: DistillationRow) => !isDistillationWorker || !canCancelExperiment(row),
    },
  ];

  useEffect(() => {
    const filteredArray = applyFiltersToArray(filters, myExperiments);
    setFilteredExperiments(filteredArray);
  }, [filters, myExperiments]);

  return (
    <div>
      <UXDataTable
        tableWidth="100%"
        cols={MyDistillationCols}
        rows={filteredExperiments}
        moreOptionsCell={true}
        enablePaging={true}
        noDataMessage={noDataMessage}
        menuProps={menuItems}
        defaultRowsPerPage={10}
        isDataLoading={isDataLoading}
        tableName={"DistillationExperiments"}
        dataRefreshTrigger={false}
        setDataRefreshTrigger={() => {}}
        enableSorting={true}
      />

      <ProgressSpinner display={isProcessingAction} />

      {activeModal && <ActionModal modal={activeModal} onClose={closeModal} open={!!activeModal} />}
    </div>
  );
};

export default TableDistillationsBacklog;
